import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientAPI } from '../../services/api';
import { Mail, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const ClientLogin = () => {
  const [emailOrTelegram, setEmailOrTelegram] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verifying, setVerifying] = useState(false);
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Check for token in URL (magic link verification)
  useEffect(() => {
    if (token) {
      verifyToken(token);
    }
  }, [token]);

  // Check if already logged in
  useEffect(() => {
    const clientId = localStorage.getItem('clientId');
    const clientToken = localStorage.getItem('clientToken');
    if (clientId && clientToken && !token) {
      navigate('/cabinet');
    }
  }, [navigate, token]);

  const verifyToken = async (tokenValue) => {
    setVerifying(true);
    setError('');
    try {
      const response = await clientAPI.verifyToken(tokenValue);
      if (response.success) {
        localStorage.setItem('clientId', response.client_id);
        localStorage.setItem('clientToken', response.token);
        navigate('/cabinet');
      }
    } catch (err) {
      setError('Ссылка недействительна или устарела. Запросите новую.');
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!emailOrTelegram.trim()) return;

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await clientAPI.requestMagicLink(emailOrTelegram.trim());
      if (response.success) {
        setSuccess(true);
        // For development: auto-login with the magic link
        if (response.magic_link) {
          const tokenMatch = response.magic_link.match(/token=([^&]+)/);
          if (tokenMatch) {
            setTimeout(() => {
              verifyToken(tokenMatch[1]);
            }, 1500);
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Клиент не найден. Проверьте данные.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--cream)',
        padding: 'var(--space-xl)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 
            size={48} 
            style={{ 
              color: 'var(--accent-gold)', 
              animation: 'spin 1s linear infinite' 
            }} 
          />
          <p style={{ 
            marginTop: 'var(--space-l)', 
            fontFamily: 'var(--font-body)',
            color: 'var(--mid-gray)'
          }}>
            Проверяем вашу ссылку...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--cream)',
      padding: 'var(--space-xl)'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: 'var(--white)',
        padding: 'var(--space-xxl)',
        boxShadow: 'var(--shadow-subtle)'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-xxl)' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.75rem',
            color: 'var(--black)',
            marginBottom: 'var(--space-s)'
          }}>
            Личный кабинет
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            color: 'var(--mid-gray)'
          }}>
            Наталья Ромашова • Стилист
          </p>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }} data-testid="magic-link-success">
            <CheckCircle size={48} style={{ color: '#22c55e', marginBottom: 'var(--space-l)' }} />
            <h3 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.5rem',
              marginBottom: 'var(--space-m)'
            }}>
              Ссылка отправлена!
            </h3>
            <p style={{
              fontFamily: 'var(--font-body)',
              fontSize: '0.875rem',
              color: 'var(--mid-gray)',
              lineHeight: 1.6
            }}>
              Проверьте почту или Telegram. Ссылка действует 24 часа.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="login-form">
            <div style={{ marginBottom: 'var(--space-l)' }}>
              <label style={{
                display: 'block',
                fontFamily: 'var(--font-body)',
                fontSize: '0.75rem',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: 'var(--mid-gray)',
                marginBottom: 'var(--space-s)'
              }}>
                Email или Telegram
              </label>
              <div style={{ position: 'relative' }}>
                <Mail 
                  size={18} 
                  style={{ 
                    position: 'absolute', 
                    left: '16px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: 'var(--light-gray)'
                  }} 
                />
                <input
                  type="text"
                  value={emailOrTelegram}
                  onChange={(e) => setEmailOrTelegram(e.target.value)}
                  placeholder="email@example.com или @username"
                  data-testid="login-input"
                  style={{
                    width: '100%',
                    padding: '16px 16px 16px 48px',
                    fontFamily: 'var(--font-body)',
                    fontSize: '1rem',
                    border: '1px solid var(--soft-gray)',
                    outline: 'none',
                    transition: 'border-color 300ms ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--accent-gold)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--soft-gray)'}
                />
              </div>
            </div>

            {error && (
              <div 
                data-testid="login-error"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  padding: 'var(--space-m)',
                  backgroundColor: '#fef2f2',
                  marginBottom: 'var(--space-l)'
                }}
              >
                <AlertCircle size={18} style={{ color: '#ef4444' }} />
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: '#ef4444'
                }}>
                  {error}
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !emailOrTelegram.trim()}
              data-testid="login-submit-btn"
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-s)',
                padding: '18px',
                backgroundColor: loading || !emailOrTelegram.trim() ? 'var(--light-gray)' : 'var(--black)',
                color: 'var(--white)',
                border: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: '0.8125rem',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                cursor: loading || !emailOrTelegram.trim() ? 'not-allowed' : 'pointer',
                transition: 'all 300ms ease'
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Отправляем...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Получить ссылку
                </>
              )}
            </button>
          </form>
        )}

        <p style={{
          textAlign: 'center',
          marginTop: 'var(--space-xl)',
          fontFamily: 'var(--font-body)',
          fontSize: '0.75rem',
          color: 'var(--light-gray)'
        }}>
          Для доступа к кабинету свяжитесь со стилистом
        </p>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ClientLogin;
